import apiClient from './client'

import type {
  DietaEntryInput,
  DietaFilterOptionsResponse,
  DietaSheet,
  DietaSheetListItem,
  DietaType,
  DietaXlsConcept,
  PaginatedResponse
} from '@/types/gia'

interface DietaFilters {
  page?: number
  centro?: number
  trabajador?: number
  year?: number
  month?: number
  status?: 'draft' | 'closed'
}

const dietasApi = {
  list: async (filters: DietaFilters = {}): Promise<PaginatedResponse<DietaSheetListItem>> => {
    const response = await apiClient.get<PaginatedResponse<DietaSheetListItem>>('/dietas/', {
      params: filters
    })

    return response.data
  },

  get: async (id: number): Promise<DietaSheet> => {
    const response = await apiClient.get<DietaSheet>(`/dietas/${id}/`)

    return response.data
  },

  listTypes: async (): Promise<DietaType[]> => {
    const response = await apiClient.get<DietaType[]>('/dietas-types/')

    return response.data
  },

  createType: async (data: Partial<DietaType>): Promise<DietaType> => {
    const response = await apiClient.post<DietaType>('/dietas-types/', data)

    return response.data
  },

  updateType: async (id: number, data: Partial<DietaType>): Promise<DietaType> => {
    const response = await apiClient.put<DietaType>(`/dietas-types/${id}/`, data)

    return response.data
  },

  deleteType: async (id: number) => {
    await apiClient.delete(`/dietas-types/${id}/`)
  },

  listXlsConcepts: async (): Promise<DietaXlsConcept[]> => {
    const response = await apiClient.get<DietaXlsConcept[]>('/dietas-xls-concepts/')

    return response.data
  },

  createXlsConcept: async (data: Partial<DietaXlsConcept>): Promise<DietaXlsConcept> => {
    const response = await apiClient.post<DietaXlsConcept>('/dietas-xls-concepts/', data)

    return response.data
  },

  updateXlsConcept: async (id: number, data: Partial<DietaXlsConcept>): Promise<DietaXlsConcept> => {
    const response = await apiClient.put<DietaXlsConcept>(`/dietas-xls-concepts/${id}/`, data)

    return response.data
  },

  deleteXlsConcept: async (id: number) => {
    await apiClient.delete(`/dietas-xls-concepts/${id}/`)
  },

  createEntry: async (data: DietaEntryInput) => {
    const response = await apiClient.post('/dietas/entries/', data)

    return response.data
  },

  updateEntry: async (id: number, data: Partial<DietaEntryInput>) => {
    const response = await apiClient.patch(`/dietas/entries/${id}/`, data)

    return response.data
  },

  deleteEntry: async (id: number) => {
    await apiClient.delete(`/dietas/entries/${id}/`)
  },

  exportXls: async (params: { month?: number | 'all'; year?: number; centro?: number | 'all' }): Promise<Blob> => {
    const response = await apiClient.get('/dietas/export/', {
      params,
      responseType: 'blob'
    })

    return response.data
  },

  listFilterOptions: async (filters: Omit<DietaFilters, 'page' | 'trabajador' | 'status'> = {}): Promise<DietaFilterOptionsResponse> => {
    const response = await apiClient.get<DietaFilterOptionsResponse>('/dietas/filter-options/', {
      params: filters
    })

    return response.data
  }
}

export default dietasApi
